import { apiFetch } from '@shared/auth';
import { DEFAULT_PORTION } from '@diet/types';
import type {
  DietEntry,
  DietEntryDraft,
  EstimateQuota,
  EstimateResult,
  MealSlot,
  MeasurementUnit,
  Nutrients,
  Portion,
} from '@diet/types';
import { shiftDateKey, todayKey } from '@shared/utils';

/*
 * 資料存取邊界。
 *
 * 呼叫的是 auth-service（BFF）代理過的 diet-service —— 前端沒有 access token，
 * 不能直接打 api.codeincold.com（見 @shared/auth 的 api.ts）。
 *
 * 這裡也是 API 格式與前端型別的翻譯層：後端的餐別是大寫 enum，前端用小寫字面量。
 * 兩邊各自維持自己的慣例，轉換只發生在這一支。
 */

const ENTRIES_PATH = '/api/diet/entries';
const ESTIMATES_PATH = '/api/diet/nutrition-estimates';

/**
 * 一次載入的天數。
 *
 * 足以涵蓋「常吃品項」的統計來源，同時避免長期使用後把整份歷史撈回瀏覽器。
 * 後端上限為 366 天，這個值必須小於它。
 */
const LOAD_WINDOW_DAYS = 90;

/** 後端回傳的紀錄格式。 */
interface DietEntryPayload {
  id: string;
  date: string;
  name: string;
  calories: number;
  slot: Uppercase<MealSlot>;
  /* 後端未填的欄位可能整組缺席，也可能是各欄位為 null，兩種都要當「未填」處理 */
  nutrients: Partial<Nutrients> | null;
  portion: {
    quantity: number;
    unit: Uppercase<MeasurementUnit>;
    referencePortionG: number | null;
  } | null;
}

/**
 * 取得紀錄。
 *
 * 日期區間由前端算並明確送出：只有瀏覽器知道使用者在哪個時區，交給後端算「今天」
 * 會在跨時區時差一天。
 */
export async function fetchEntries(): Promise<DietEntry[]> {
  const to = todayKey();
  const from = shiftDateKey(to, -(LOAD_WINDOW_DAYS - 1));

  const payload = await apiFetch<DietEntryPayload[]>(
    `${ENTRIES_PATH}?from=${from}&to=${to}`,
  );
  return payload.map(toDietEntry);
}

export async function createEntry(draft: DietEntryDraft): Promise<DietEntry> {
  const payload = await apiFetch<DietEntryPayload>(ENTRIES_PATH, {
    method: 'POST',
    data: toRequestBody(draft),
  });
  /* id 由後端產生，回傳的內容才是這筆紀錄的真實樣貌，不用本地的 draft 頂替 */
  return toDietEntry(payload);
}

/** 改寫整筆紀錄（後端為 PUT 語意：以送出的內容取代原內容）。 */
export async function updateEntry(entry: DietEntry): Promise<DietEntry> {
  const payload = await apiFetch<DietEntryPayload>(
    `${ENTRIES_PATH}/${entry.id}`,
    {
      method: 'PUT',
      data: toRequestBody(entry),
    },
  );
  return toDietEntry(payload);
}

export async function deleteEntry(id: string): Promise<string> {
  await apiFetch<void>(`${ENTRIES_PATH}/${id}`, { method: 'DELETE' });
  return id;
}

/** 新增與編輯共用同一組請求欄位（後端也是同一個型別）。 */
function toRequestBody(entry: DietEntryDraft) {
  return {
    date: entry.date,
    name: entry.name,
    calories: entry.calories,
    slot: entry.slot.toUpperCase(),
    nutrients: entry.nutrients,
    portion: {
      quantity: entry.portion.quantity,
      unit: entry.portion.unit.toUpperCase(),
      referencePortionG: entry.portion.referencePortionG,
    },
  };
}

/*
 * 後端可能整組省略 nutrients，也可能各欄位為 null。統一收斂成「每個 key 都在、
 * 值為 number 或 null」，讓元件不必為「有沒有這個 key」多寫一條分支。
 */
function toNutrients(
  payload: Partial<Nutrients> | null | undefined,
): Nutrients {
  return {
    proteinG: payload?.proteinG ?? null,
    fatG: payload?.fatG ?? null,
    carbsG: payload?.carbsG ?? null,
    fiberG: payload?.fiberG ?? null,
    sodiumMg: payload?.sodiumMg ?? null,
  };
}

/**
 * 以 AI 估算食物的營養成分。
 *
 * 失敗時 `apiFetch` 會拋出，錯誤處理留給呼叫端 —— 它才知道該顯示哪種提示，
 * 以及這次失敗值不值得讓使用者重試。
 */
export async function estimateNutrition(
  foodName: string,
  quantity: number,
  unit: MeasurementUnit,
): Promise<EstimateResult> {
  /*
   * 回應把估算與額度包在一起，因為兩者在同一次請求裡產生。分開拿會有一段
   * 「已經扣了但畫面還沒更新」的空窗，而剩餘次數正是按下按鈕後最想確認的東西。
   */
  return apiFetch<EstimateResult>(ESTIMATES_PATH, {
    method: 'POST',
    data: { foodName, quantity, unit: unit.toUpperCase() },
  });
}

/**
 * 查今日剩餘估算次數，不扣除。
 *
 * 開啟表單時取一次，讓「還能按幾次」在按下去之前就看得到。
 */
export async function fetchEstimateQuota(): Promise<EstimateQuota> {
  return apiFetch<EstimateQuota>(`${ESTIMATES_PATH}/quota`);
}

function toDietEntry(payload: DietEntryPayload): DietEntry {
  return {
    id: payload.id,
    date: payload.date,
    name: payload.name,
    calories: payload.calories,
    slot: payload.slot.toLowerCase() as MealSlot,
    nutrients: toNutrients(payload.nutrients),
    portion: toPortion(payload.portion),
  };
}

/* 既有紀錄沒有份量欄位，一律視為一份、無換算基準 */
function toPortion(payload: DietEntryPayload['portion']): Portion {
  if (payload === null || payload === undefined) return DEFAULT_PORTION;

  return {
    quantity: payload.quantity,
    unit: payload.unit.toLowerCase() as MeasurementUnit,
    referencePortionG: payload.referencePortionG ?? null,
  };
}
